import FunctionHelper from "../helper/FunctionHelper";

/**
 * A ConcurrentOperation is used to wrap multiple promises and execute them sequentially, while the concurrent
 * executions count can be set.
 */
export default class ConcurrentOperation {
	private tasks: Array<(resolve: () => void, reject: (exception: Error) => void) => Promise<void>>;
	private concurrentTasks: number;

	/**
	 * Constructs an {@link ConcurrentOperation} instance
	 */
	constructor(concurrentTasks: number) {
		this.concurrentTasks = concurrentTasks;
		this.tasks = [];
	}

	/**
	 * Adds a task to execute
	 */
	public add(task: (resolve: () => void, reject: (exception: Error) => void) => Promise<void>) {
		this.tasks.push(task);
	}

	/**
	 * Executes the currently set tasks sequentially in the set concurrentTasks steps
	 */
	public async execute(startIndex?: number): Promise<void> {
		let tasks: Array<Promise<void>> = [];
		let current: number = FunctionHelper.exists(startIndex) ? startIndex! : 0;

		for (let i = 0; current + i < this.tasks.length; i++) {
			tasks.push(new Promise(this.tasks[current + i]));

			if (tasks.length >= this.concurrentTasks) {
				break;
			}
		}

		try {
			await Promise.all(tasks);
		} catch (e) {
			throw e;
		}

		if (current + tasks.length < this.tasks.length) {
			await this.execute(current + tasks.length);
		}
	}

	/**
	 * Clears the currently set tasks
	 */
	public clear(): void {
		this.tasks = [];
	}

	/**
	 * Sets the number of concurrent tasks
	 */
	public setConcurrentTasks(value: number): void {
		this.concurrentTasks = value;
	}

	public get length(): number {
		return this.tasks.length;
	}
}